/**
 * rechnungVersand — Rechnung önizleme/yazdırma/e-posta akışları.
 *
 * Akış (spesifikasyon):
 *  - Entwurf satırına tıklanınca RE otomatik oluşturulur + PDF ÖNİZLEME
 *    yeni sekmede açılır.
 *  - "Per E-Mail versenden": müşteriye gönderilir; başarıda
 *    invoice_sent_at damgalanır ve Fibu'ya buchulur (OPOS açılır) →
 *    durum otomatik Entwurf → Offen.
 *  - Sammelversand: seçili tüm Entwurf'lar tek tıkla sırayla gönderilir.
 */
import { base44 } from '@/api/base44Client';
import { generateDocNumber } from '@/lib/stockApi';
import { getOrderItems } from '@/lib/orderFields';
import { computeInvoiceTotals, skontoInfo } from '@/lib/invoiceCalc';
import { fetchPdfSettings } from '@/lib/pdfRenderer';
import { bookInvoiceFromOrder } from '@/lib/buchhaltungApi';
import { issueInvoiceFromOrder, cancelInvoice } from '@/lib/invoiceApi';
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';
import { buildRechnungHtml } from '@/components/orders/RechnungPDF';

const eur = (v) => Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/**
 * Rechnung festschreiben: Nummer + Buchung + offener Posten in EINER
 * DB-Transaktion (idempotent). Gibt den Auftrag mit Rechnungsdaten zurück.
 */
export async function ensureInvoice(order, actorEmail) {
  try {
    const inv = await issueInvoiceFromOrder(order.id, actorEmail);
    return {
      ...order,
      invoice_number: inv.invoice_no,
      invoiced_at: order.invoiced_at || inv.finalized_at || new Date().toISOString(),
      _invoice: inv,
    };
  } catch (err) {
    if (err.code !== 'RPC_FEHLT') throw err;
  }
  // Fallback, solange Migration 20261016 nicht ausgeführt ist
  if (order.invoice_number) return order;
  const invoiceNumber = await generateDocNumber('RE');
  if (!invoiceNumber) throw new Error('RE-Nummer konnte nicht erzeugt werden.');
  const now = new Date().toISOString();
  const updated = await base44.entities.Order.update(order.id, {
    invoice_number: invoiceNumber,
    invoiced_at: now,
    invoiced_by: actorEmail || null,
    updated_date: now,
  });
  return { ...order, ...updated, invoice_number: invoiceNumber, invoiced_at: now };
}

/** Fatura HTML'ini yeni sekmede ÖNİZLEME olarak açar (yazdırma tetiklenmez). */
export async function openRechnungPreview(order) {
  const html = await buildRechnungHtml(order);
  const win = window.open('', '_blank');
  if (!win) throw new Error('Pop-up blockiert — bitte Pop-ups erlauben.');
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.document.title = `Rechnung ${order.invoice_number || ''}`;
}

/** Fatura HTML'ini açıp yazdırma diyaloğunu tetikler. */
export async function printRechnung(order) {
  const html = await buildRechnungHtml(order);
  const win = window.open('', '_blank');
  if (!win) throw new Error('Pop-up blockiert — bitte Pop-ups erlauben.');
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 600);
}

/** E-posta gövdesi (düz metin fatura özeti). */
function invoiceEmailBody(order, settings, lead) {
  const items = getOrderItems(order);
  const lines = items.map((it) =>
    `  ${it.quantity}x ${it.product_name || it.name} — ${eur((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.rabatt_prozent) || 0) / 100))}`
  );
  // Betrag und Bedingungen exakt wie auf der Rechnung (lib/invoiceCalc.js)
  const t = computeInvoiceTotals(items, {
    taxRate: Number(order.tax_rate ?? 19),
    kundenrabattProzent: Number(lead?.kundenrabatt_prozent) || 0,
  });
  const ziel = Number(lead?.zahlungsziel_tage ?? 5);
  const skonto = skontoInfo({
    total: t.total,
    invoiceDate: (order.invoiced_at || new Date().toISOString()).slice(0, 10),
    skontoProzent: lead?.skonto_prozent,
    skontoTage: lead?.skonto_tage,
  });
  return [
    'Sehr geehrte Damen und Herren,',
    '',
    `anbei erhalten Sie unsere Rechnung ${order.invoice_number}.`,
    '',
    'Positionen:',
    ...lines,
    '',
    ...(t.kundenrabattBetrag > 0 ? [`Kundenrabatt ${t.kundenrabattProzent} %: −${eur(t.kundenrabattBetrag)}`] : []),
    `Gesamtbetrag: ${eur(t.total)}`,
    ziel > 0 ? `Zahlbar innerhalb von ${ziel} Tagen.` : 'Zahlbar sofort.',
    ...(skonto ? [`Bei Zahlung bis ${skonto.bis.split('-').reverse().join('.')}: ${eur(skonto.zahlbetrag)} (${lead.skonto_prozent} % Skonto).`] : []),
    `Verwendungszweck: ${order.invoice_number}`,
    '',
    'Mit freundlichen Grüßen',
    settings.company_name || 'InMotion Handel',
  ].join('\n');
}

/**
 * Faturayı e-posta ile gönderir; başarıda:
 *  1) invoice_sent_at/to damgalanır (Entwurf → Offen geçişinin kaynağı)
 *  2) Fibu'ya buchulur — OPOS açılır (idempotent; zaten buchulmuşsa atlanır)
 * @returns {string} gönderilen adres
 */
export async function sendRechnungEmail(order, actorEmail) {
  const to = order.lead_email;
  if (!to) throw new Error(`${order.invoice_number || order.order_number}: Keine E-Mail-Adresse beim Kunden.`);

  const finalized = await ensureInvoice(order, actorEmail);
  const settings = await fetchPdfSettings();
  const lead = finalized.lead_id ? await base44.entities.Lead.get(finalized.lead_id).catch(() => null) : null;

  await base44.integrations.Core.SendEmail({
    to,
    subject: `Rechnung ${finalized.invoice_number} — ${settings.company_name || 'InMotion Handel'}`,
    body: invoiceEmailBody(finalized, settings, lead),
  });

  const now = new Date().toISOString();
  await base44.entities.Order.update(finalized.id, {
    invoice_sent_at: now,
    invoice_sent_to: to,
    updated_date: now,
  });
  if (finalized._invoice?.id) {
    // Versandvermerk an der Rechnung (bei festgeschriebenen Rechnungen erlaubt)
    await SupabaseAdapter.getClient().from('fibu_invoices')
      .update({ sent_at: now, sent_to: to }).eq('id', finalized._invoice.id)
      .then(({ error }) => { if (error) console.warn('[rechnungVersand] Versandvermerk:', error.message); });
  }

  // Bereits über ensureInvoice gebucht; nur der Fallback-Weg bucht hier noch.
  if (finalized._invoice) return to;
  try {
    await bookInvoiceFromOrder(finalized, actorEmail);
  } catch (err) {
    if (!(err.message || '').includes('bereits gebucht')) {
      console.warn('[rechnungVersand] Fibu-Buchung fehlgeschlagen:', err.message);
    }
  }
  return to;
}

/**
 * Sammelversand: sıralı gönderim, hata toleranslı.
 * @returns {{sent: number, errors: string[]}}
 */
export async function sendRechnungenBulk(orders, actorEmail) {
  let sent = 0;
  const errors = [];
  for (const order of orders) {
    try {
      await sendRechnungEmail(order, actorEmail);
      sent++;
    } catch (err) {
      errors.push(err.message);
    }
  }
  return { sent, errors };
}

/**
 * Rechnung stornieren.
 * Festgeschriebene Rechnung → Stornorechnung (SR-…) mit Gegenbuchung,
 * offener Posten geschlossen, bereits Gezahltes wird Kundenguthaben (GoBD:
 * nichts wird gelöscht). Nur ein Entwurf ohne Rechnung wird bloß markiert.
 * @returns {Promise<{ storno_no?: string, guthaben?: number }>}
 */
export async function stornoRechnung(order, { invoiceId, reason, actorEmail } = {}) {
  if (invoiceId) {
    return cancelInvoice(invoiceId, reason, actorEmail);
  }
  const now = new Date().toISOString();
  await base44.entities.Order.update(order.id, {
    invoice_storniert_at: now,
    updated_date: now,
  });
  return {};
}
